// BerryEditor 版本更新检查
// 通过 GitHub Releases API 检查最新版本

const GITHUB_REPO = 'berry-editor/scratch-gui';
const API_URL = `https://api.github.com/repos/${GITHUB_REPO}/releases/latest`;
const STORAGE_KEY = 'berry:lastSeenVersion';

// 当前版本，由 webpack DefinePlugin 注入
// 开发环境下回退到 '0.0.00' 避免报错
const CURRENT_VERSION = (typeof process !== 'undefined' &&
    process.env && process.env.VERSION) || '0.0.00';

/**
 * 将版本号字符串转为可比较的数字数组
 * 支持格式: "0.0.02", "v0.0.03", "1.2.3"
 * @param {string} version 版本号字符串
 * @returns {number[]} 数字数组，如 [0, 0, 2]
 */
const parseVersion = version => {
    const cleaned = String(version)
        .replace(/^v/i, '')
        .trim();
    return cleaned.split('.').map(part => {
        const num = parseInt(part, 10);
        return isNaN(num) ? 0 : num;
    });
};

/**
 * 比较两个版本号
 * @param {string} a 版本号 a
 * @param {string} b 版本号 b
 * @returns {number} 正数表示 a 更新，负数表示 b 更新，0 表示相同
 */
const compareVersions = (a, b) => {
    const partsA = parseVersion(a);
    const partsB = parseVersion(b);
    const maxLen = Math.max(partsA.length, partsB.length);
    for (let i = 0; i < maxLen; i++) {
        const diff = (partsA[i] || 0) - (partsB[i] || 0);
        if (diff !== 0) return diff;
    }
    return 0;
};

/**
 * 检查 GitHub 是否有新版本
 * @returns {Promise<object|null>} 新版本信息，若没有新版本或出错则返回 null
 *   - latestVersion: string 最新版本号
 *   - releaseUrl: string Release 页面链接
 *   - releaseNotes: string 更新日志（Markdown 纯文本）
 */
const checkForUpdate = async () => {
    try {
        const headers = {Accept: 'application/vnd.github.v3+json'};
        // 使用构建时注入的 GitHub Token 提高请求限额
        if (typeof process !== 'undefined' && process.env && process.env.GITHUB_TOKEN) {
            headers.Authorization = `token ${process.env.GITHUB_TOKEN}`;
        }
        const response = await fetch(API_URL, {headers});
        if (!response.ok) return null;

        const data = await response.json();
        const latestVersion = data.tag_name;
        if (!latestVersion) return null;

        // 当前版本 >= 最新版本，无需更新
        if (compareVersions(CURRENT_VERSION, latestVersion) >= 0) {
            return null;
        }

        // 用户已经看过这个版本的更新提示，不再弹出
        const lastSeen = localStorage.getItem(STORAGE_KEY);
        if (lastSeen && compareVersions(lastSeen, latestVersion) >= 0) {
            return null;
        }

        return {
            currentVersion: CURRENT_VERSION,
            latestVersion: latestVersion,
            releaseUrl: data.html_url || `https://github.com/${GITHUB_REPO}/releases`,
            releaseNotes: data.body || ''
        };
    } catch (e) {
        // 网络错误等，静默处理
        return null;
    }
};

/**
 * 标记某个版本已被用户查看过
 * @param {string} version 版本号
 */
const markVersionSeen = version => {
    try {
        localStorage.setItem(STORAGE_KEY, version);
    } catch (e) {
        // localStorage 不可用时忽略
    }
};

export {
    checkForUpdate,
    markVersionSeen,
    CURRENT_VERSION
};
